// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import type { Artifact, ProcedureDetail } from '../types/procedures';

/**
 * Inverse of `toProcedureDetail()` — maps the guided-form's editable shape
 * back onto the real `Artifact` envelope for saving. `original` supplies
 * everything this screen doesn't edit (`payload_ref`, `relationships`,
 * `provenance`, `approval`, etc.) so a save never drops fields it never
 * touched.
 *
 * Only fields `toProcedureDetail()` actually reads from are written back
 * here. `prerequisites`, `contingencies`, and `closeout` have no upstream
 * field yet (see `ProcedureDetail`'s docstring) and are intentionally left
 * out rather than guessed at — wire them in once SOP Factory's create/update
 * contract defines where they go, or they'll silently vanish on save.
 */
export function fromProcedureDetail(procedure: ProcedureDetail, original: Artifact): Artifact {
  return {
    ...original,
    // `toProcedureDetail` reads `procedureNumber` from this same field. On a
    // brand-new draft `artifactId` is blank and `procedureNumber` is the one
    // the user actually types into the guided form — write that back, or a
    // create/save silently goes out with an empty artifact id.
    artifact_id: procedure.procedureNumber || procedure.artifactId,
    artifact_type: procedure.artifactType,
    title: procedure.title,
    version: procedure.version,
    status: procedure.status,
    categories: {
      ...original.categories,
      domain: procedure.classification,
    },
    payload: {
      ...original.payload,
      equipment_type: procedure.equipmentType,
      description_of_work: procedure.description,
      affected_equipment: procedure.affectedEquipment.map(item => ({
        role: item.role,
        type: item.type,
        asset_binding: item.asset,
      })),
      steps: procedure.procedure.map(step => ({
        seq: step.step,
        instruction: step.action,
        check_type: step.check,
        is_gate: step.gate,
        citations: step.source ? [step.source] : [],
      })),
      governance: {
        mop_required: procedure.governance.mopRequired,
        change_approval: procedure.governance.changeApproval,
        loto: procedure.governance.loto,
        redundancy: procedure.governance.redundancy,
        escalates_to_eop: procedure.governance.escalatesToEop,
      },
    },
  };
}
